import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { ManagedDomEventListeners } from '@ipfabric/ui-components/utils/dom/ManagedDomEventListeners';
import { ESCAPE } from '@ipfabric/ui-components/utils/keyboard/constants';
import { Button } from 'components/Button/Button';
import { TextInput } from 'components/TextInput/TextInput';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, useFormik } from 'formik';
import { useEffect, useRef } from 'react';
import type { FC } from 'react';
import { fieldHasError } from 'utils/formik/fieldHasError';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';

interface Props {
  onClose: () => void;
  onSubmit: (values: SaveViewFormValues) => void;
}

export interface SaveViewFormValues {
  name: string;
}

const initialValues: SaveViewFormValues = {
  name: '',
};

export const SaveViewForm: FC<Props> = (props) => {
  const msg = useLocale();
  const nameInputRef = useRef<HTMLInputElement>(null);

  const handleKeyDown = useStableCallback((event: KeyboardEvent) => {
    if (event.which === ESCAPE) {
      props.onClose();
    }
  });

  useEffect(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const eventListeners = new ManagedDomEventListeners([
      document,
      'keydown',
      handleKeyDown,
    ]);

    return () => {
      eventListeners.remove();
    };
  }, []);

  const formik = useFormik({
    initialValues,
    onSubmit: makeFormikOnSubmit((values: SaveViewFormValues) => {
      props.onSubmit(values);
    }),
    validate: ({ name }) => {
      const errors: FormikErrors<SaveViewFormValues> = {};

      if (!name) {
        errors.name = msg.canvasGraph.commands.saveView.nameRequired;
      }

      return errors;
    },
  });

  const field = formik.getFieldProps('name');
  const hasError = fieldHasError<SaveViewFormValues>(formik, 'name');

  return (
    <form onSubmit={formik.handleSubmit} className='graph-toolbar__form'>
      <TextInput
        {...field}
        autoComplete='off'
        hasError={hasError}
        inputRef={nameInputRef}
        placeholder={msg.canvasGraph.commands.saveView.typeName}
        suffixText={
          <Button variant='tertiary' icon='fa-check' size='xs' type='submit' />
        }
        type='text'
      />
    </form>
  );
};
